import { useCombobox, useMultipleSelection } from 'downshift';
import { useState } from 'react';

import { Option } from '@/fo/ui/kit/listbox/base';

export interface UseMultipleSelectProps<T extends Option> {
  options: T[];
  onChange?: (selectedOptions: T[]) => void;
}

export function useMultipleSelect<T extends Option>({
  options,
  onChange
}: UseMultipleSelectProps<T>) {
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);

  const filteredOptions = options.filter(
    (option) =>
      !selectedOptions.some((selected) => selected.value === option.value) &&
      option.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  const { getDropdownProps, removeSelectedItem } = useMultipleSelection({
    selectedItems: selectedOptions,
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          setSelectedOptions(newSelectedItems || []);
          onChange?.(newSelectedItems || []);
          break;
        default:
          break;
      }
    }
  });

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps
  } = useCombobox({
    items: filteredOptions,
    itemToString: (item) => item?.label || '',
    defaultHighlightedIndex: 0,
    selectedItem: null,
    inputValue,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: 0
          };
        default:
          return changes;
      }
    },
    onStateChange: ({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem
    }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (newSelectedItem) {
            setSelectedOptions([...selectedOptions, newSelectedItem]);
            onChange?.([...selectedOptions, newSelectedItem]);
            setInputValue('');
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue || '');
          break;
        default:
          break;
      }
    }
  });

  const handleRemoveOption = (optionToRemove: T) => {
    removeSelectedItem(optionToRemove);
  };

  const handleSelectionChange = (keys: 'all' | Iterable<React.Key>) => {
    // Обрабатываем выбор через react-aria
    if (keys !== 'all') {
      const keysArray = Array.from(keys);
      const lastSelectedKey = keysArray[keysArray.length - 1];
      const selectedOption = filteredOptions.find(
        (option) => option.value === lastSelectedKey
      );
      if (selectedOption) {
        const isAlreadySelected = selectedOptions.some(
          (item: T) => item.value === selectedOption.value
        );
        if (!isAlreadySelected) {
          setSelectedOptions([...selectedOptions, selectedOption]);
          onChange?.([...selectedOptions, selectedOption]);
        }
      }
    }
  };

  return {
    // Состояние
    inputValue,
    isOpen: filteredOptions.length > 0 && isOpen,
    filteredOptions,
    highlightedIndex,
    selectedOptions,

    // Обработчики
    handleRemoveOption,
    handleSelectionChange,

    // Downshift пропсы
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    getDropdownProps
  };
}
